/**
 * bpidm.ts — draw the BP channel's 021 IDM (card `t_6f7db3f5`). Compute nothing.
 *
 * Every number arrives from `/api/overlays/{a}/{tf}/bp_idm`, which is `ict.bp_idm` (bp_method.md
 * §1.8, lesson 021 10:00-10:40; the rules and their timecodes live in that module). This file maps
 * rows to shapes and decides no rule — `overlays.ts`' contract: geometry in, pixels out.
 *
 *   level    a dashed line at the pullback extreme, from the bar that made it to the bar whose BODY
 *            closed through it («закрепление телом», 021 10:40), or to the right edge while live.
 *            Nothing is drawn for an IDM before its forming close: the server emits a row only
 *            once a body closed beyond the leg extreme («именно телом свечи», 021 10:12).
 *   formed   a small marker on the forming bar (`IDM↑` / `IDM↓`).
 *   broken   a marker on the breaking bar (`brk`); the broken line stays, grey and unlabelled.
 *
 * Distinct from the `setup` layer's IDM (`IDM_LEVEL_COLOR`, `ict.idm.find_inducements`), which
 * is a different rule: amber here, `BP IDM` on the plate. OFF BY DEFAULT. NOT A SIGNAL.
 */
import type { SeriesMarker, Time, UTCTimestamp } from 'lightweight-charts';
import type { ZoneBand } from './overlays';

export type BpIdmRow = {
  side: 'up' | 'down';
  level: number;
  level_idx: number;
  high_idx: number;
  pullback_idx: number;
  formed_idx: number;
  broken_idx: number | null;
  time: UTCTimestamp;
  level_time: UTCTimestamp;
  formed_time: UTCTimestamp;
  broken_time: UTCTimestamp | null;
  end_time: UTCTimestamp | null;
};

export type BpIdmPayload = {
  component: 'bp_idm';
  source: string;
  counts: { total: number };
  idms: BpIdmRow[];
};

export const BP_IDM_STYLE = {
  up: '#ffa000',
  down: '#ff6f00',
  broken: 'rgba(140, 140, 140, 0.45)',
};

/** A shape that began before the pane's first bar starts at that bar (the `sessions.ts` clamp). */
const clamp = (t: UTCTimestamp, first: number | null): UTCTimestamp =>
  (first != null && t < first ? first : t) as UTCTimestamp;

export const bpIdmLabel = (r: BpIdmRow): string => `BP IDM ${r.side === 'up' ? '↑' : '↓'}`;

/** One degenerate dashed band per IDM; a broken one ending left of the pane is not drawn. */
export function bpIdmBands(p: BpIdmPayload, windowFirstTime: number | null = null): ZoneBand[] {
  const out: ZoneBand[] = [];
  for (const r of p.idms) {
    if (!Number.isFinite(r.level)) continue;
    const live = r.broken_time == null;
    if (!live && windowFirstTime != null && (r.broken_time as number) < windowFirstTime) continue;
    const color = live ? BP_IDM_STYLE[r.side] : BP_IDM_STYLE.broken;
    out.push({
      lo: r.level,
      hi: r.level,
      fromTime: clamp(r.level_time, windowFirstTime),
      toTime: live ? null : r.broken_time,
      fill: color,
      border: color,
      dashed: true,
      // A live level runs to the edge: its plate goes where the eye is (`idmLevels`' rule).
      plateRight: live,
      label: live ? bpIdmLabel(r) : '',
      midline: null,
      exactSpan: true,
    });
  }
  return out;
}

/** `IDM↑`/`IDM↓` on the forming bar, `brk` on the breaking bar; none left of the pane. */
export function bpIdmMarkers(p: BpIdmPayload, windowFirstTime: number | null = null): SeriesMarker<Time>[] {
  const inWin = (t: number) => windowFirstTime == null || t >= windowFirstTime;
  const out: SeriesMarker<Time>[] = [];
  for (const r of p.idms) {
    const up = r.side === 'up';
    if (inWin(r.formed_time)) {
      out.push({
        time: r.formed_time,
        position: up ? 'aboveBar' : 'belowBar',
        color: BP_IDM_STYLE[r.side],
        shape: up ? 'arrowUp' : 'arrowDown',
        text: `IDM${up ? '↑' : '↓'}`,
      });
    }
    if (r.broken_time != null && inWin(r.broken_time)) {
      out.push({
        time: r.broken_time,
        position: up ? 'belowBar' : 'aboveBar',
        color: BP_IDM_STYLE[r.side],
        shape: 'circle',
        text: 'brk',
      });
    }
  }
  return out.sort((a, b) => (a.time as number) - (b.time as number));
}

export function bpIdmSummary(p: BpIdmPayload): [string, string][] {
  const live = p.idms.filter((r) => r.broken_time == null);
  const side = (s: 'up' | 'down') => live.filter((r) => r.side === s).length;
  return [
    ['BP IDM (021)', `${p.idms.length} in window, ${p.idms.length - live.length} body-broken`],
    ['live ↑ / ↓', `${side('up')} / ${side('down')} — formed by a body close, broken by one (021 10:12, 10:40)`],
    ['what this is', 'closed bars only, inside bars skipped — NOT the setup IDM, NOT an entry signal'],
  ];
}

/** ONE CALL for every shape and the rows describing them (`bpImbalanceDrawing`'s shape). */
export function bpIdmDrawing(p: BpIdmPayload, windowFirstTime: number | null = null): {
  bands: ZoneBand[];
  markers: SeriesMarker<Time>[];
  rows: [string, string][];
} {
  return {
    bands: bpIdmBands(p, windowFirstTime),
    markers: bpIdmMarkers(p, windowFirstTime),
    rows: bpIdmSummary(p),
  };
}
